import * as React from 'react'
import MediaQuery from 'react-responsive'

import {
  Text,
  Breakpoints,
  InlineChildren,
  StackChildren,
} from '@doordash/design-language'

import SignupForm from '../../pages/landing/components/SignupForm'

import {
  ContentContainer,
  DescriptionContainer,
  ImageColumn,
  SectionContent,
  SectionImage,
  SectionTitle,
  SignupFormContainer,
  UnorderedList,
} from './styles'

import * as CONSTANT from './constants'

type Props = {
  city: string
  jobTypeName: string
}

const interpolateString = (str, city, jobTypeName) => {
  const mapObj = {
    city,
    jobTypeName,
  }

  // Finds {city} or {jobTypeName} in strings
  // Matches them with mapObj after stripping away braces
  return str.replace(/{city}|{jobTypeName}/gi, match => {
    const strippedKey = match.replace(/[{}]/g, '')
    if (strippedKey === 'jobTypeName' && mapObj[strippedKey]) {
      return mapObj[strippedKey].toLowerCase()
    }

    return mapObj[strippedKey]
  })
}

const upperCaseFirstLetter = string => {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

const OpportunitySection = ({ city, jobTypeName }: Props) => (
  <React.Fragment>
    <SectionTitle styles={Text.Styles.MajorPageTitle}>
      {upperCaseFirstLetter(
        interpolateString(CONSTANT.TITLE_OPPORTUNITY, city, jobTypeName)
      )}
    </SectionTitle>
    <SectionContent styles={Text.Styles.PageDescriptionBody}>
      {interpolateString(
        CONSTANT.CONTENT_OPPORTUNITY_LINE_1,
        city,
        jobTypeName
      )}
    </SectionContent>
    <SectionContent>
      <UnorderedList>
        {CONSTANT.CONTENT_OPPORTUNITY_BULLET.map(content => (
          <li>
            <Text styles={Text.Styles.PageDescriptionBody}>{content}</Text>
          </li>
        ))}
      </UnorderedList>
    </SectionContent>
    <SectionContent styles={Text.Styles.PageDescriptionBody}>
      {CONSTANT.CONTENT_OPPORTUNITY_LINE_2}
    </SectionContent>
  </React.Fragment>
)

const RequirementsSection = ({ city, jobTypeName }: Props) => (
  <React.Fragment>
    <SectionTitle styles={Text.Styles.MajorPageTitle}>
      {interpolateString(CONSTANT.TITLE_REQUIREMENTS, city, jobTypeName)}
    </SectionTitle>
    <UnorderedList>
      {CONSTANT.CONTENT_REQUIREMENTS.map(content => (
        <li>
          <Text styles={Text.Styles.PageDescriptionBody}>{content}</Text>
        </li>
      ))}
    </UnorderedList>
  </React.Fragment>
)

const WhatIsDoorDashSection = () => (
  <React.Fragment>
    <SectionTitle styles={Text.Styles.MajorPageTitle}>
      {CONSTANT.CONTENT_WHAT_IS_DOORDASH_TITLE}
    </SectionTitle>
    <SectionContent styles={Text.Styles.PageDescriptionBody}>
      {CONSTANT.CONTENT_WHAT_IS_DOORDASH_BODY}
    </SectionContent>
  </React.Fragment>
)

const HowToApplySection = ({ city, jobTypeName }: Props) => (
  <React.Fragment>
    <SectionTitle styles={Text.Styles.MajorPageTitle}>
      {CONSTANT.CONTENT_APPLY_TITLE}
    </SectionTitle>
    <SectionContent styles={Text.Styles.PageDescriptionBody}>
      {interpolateString(CONSTANT.CONTENT_APPLY_BODY, city, jobTypeName)}
    </SectionContent>
  </React.Fragment>
)

const HowLongSection = ({ city, jobTypeName }: Props) => (
  <React.Fragment>
    <SectionTitle styles={Text.Styles.MajorPageTitle}>
      {CONSTANT.CONTENT_HOW_LONG_TITLE}
    </SectionTitle>
    <SectionContent styles={Text.Styles.PageDescriptionBody}>
      {interpolateString(CONSTANT.CONTENT_HOW_LONG_BODY, city, jobTypeName)}
    </SectionContent>
  </React.Fragment>
)

const ApplicationSection = ({ city, jobTypeName }: Props) => (
  <React.Fragment>
    <SectionTitle styles={Text.Styles.MajorPageTitle}>
      {CONSTANT.CONTENT_APPLICATION_TITLE}
    </SectionTitle>
    <SectionContent styles={Text.Styles.PageDescriptionBody}>
      {interpolateString(CONSTANT.CONTENT_APPLICATION_BODY, city, jobTypeName)}
    </SectionContent>
  </React.Fragment>
)

const PaySection = ({ city, jobTypeName }: Props) => (
  <React.Fragment>
    <SectionTitle styles={Text.Styles.MajorPageTitle}>
      {interpolateString(CONSTANT.CONTENT_PAY_TITLE, city, jobTypeName)}
    </SectionTitle>
    <SectionContent>
      {CONSTANT.CONTENT_PAY_BODY.map(content => (
        <React.Fragment>
          <Text styles={Text.Styles.PageDescriptionBody}>
            {interpolateString(content, city, jobTypeName)}
          </Text>
          <br />
        </React.Fragment>
      ))}
      <Text styles={Text.Styles.Bold18} color={Text.Colors.TextAction}>
        <a
          href={CONSTANT.CONTENT_PAY_DASHER_LINK_HREF}
          style={{ color: 'inherit' }}
          target="_blank"
          rel="noopener"
        >
          {CONSTANT.CONTENT_PAY_DASHER_LINK}
        </a>
      </Text>
    </SectionContent>
  </React.Fragment>
)

const WhatNextSection = () => (
  <React.Fragment>
    <SectionTitle styles={Text.Styles.MajorPageTitle}>
      {CONSTANT.CONTENT_WHAT_NEXT_TITLE}
    </SectionTitle>
    <SectionContent>
      {CONSTANT.CONTENT_WHAT_NEXT_BODY.map(content => (
        <React.Fragment>
          <Text styles={Text.Styles.PageDescriptionBody}>{content}</Text>
          <br />
        </React.Fragment>
      ))}
    </SectionContent>
  </React.Fragment>
)

const DrivingOpportunitiesJobDescription: React.SFC<Props> = props => (
  <React.Fragment>
    <MediaQuery minWidth={Breakpoints.Desktop + 1}>
      <DescriptionContainer mobile={false}>
        <InlineChildren>
          <ContentContainer
            size={StackChildren.Sizes.Large}
            width="60%"
            mobile={false}
          >
            <OpportunitySection {...props} />
            <RequirementsSection {...props} />
            <WhatIsDoorDashSection />
            <HowToApplySection {...props} />
            <HowLongSection {...props} />
            <ApplicationSection {...props} />
            <PaySection {...props} />
            <WhatNextSection />
          </ContentContainer>
          <ImageColumn width="40%">
            <SectionImage src={CONSTANT.IMG_URL_1} mobile={true} />
            <SignupFormContainer>
              <SignupForm isContained />
            </SignupFormContainer>
          </ImageColumn>
        </InlineChildren>
      </DescriptionContainer>
    </MediaQuery>
    <MediaQuery maxWidth={Breakpoints.Desktop}>
      <DescriptionContainer mobile={true}>
        <StackChildren size={StackChildren.Sizes.Large}>
          <SectionImage src={CONSTANT.IMG_URL_1} mobile={true} />
          <ContentContainer mobile={true} size={StackChildren.Sizes.Large}>
            <OpportunitySection {...props} />
            <RequirementsSection {...props} />
            <WhatIsDoorDashSection />
            <HowToApplySection {...props} />
            <HowLongSection {...props} />
            <ApplicationSection {...props} />
            <PaySection {...props} />
            <WhatNextSection />
            <SignupForm isContained />
          </ContentContainer>
        </StackChildren>
      </DescriptionContainer>
    </MediaQuery>
  </React.Fragment>
)

export default DrivingOpportunitiesJobDescription
